/**
 * Login Context Service
 *
 * Collects device, network, and environmental context sent with every
 * authentication request (login + OTP verification).
 *
 * Data points align with what secure banking / fintech platforms capture
 * to enable fraud detection, impossible-travel analysis, and audit trails.
 *
 * Privacy: Only publicly-available browser APIs are used — no canvas
 * fingerprinting, WebGL hashing, or audio-context tricks. All data is
 * sent to OUR backend only.
 */

import deviceFingerprinting from '../infrastructure/device-fingerprinting.service.js';

class LoginContextService {
    constructor() {
        this._cachedContext = null;
        this._cacheTimestamp = 0;
        this._cacheTTL = 60_000; // 1 minute
    }

    /**
     * Collect full login context.
     * Returns a plain object ready to be merged into the login request body.
     */
    async collect() {
        const now = Date.now();
        if (this._cachedContext && (now - this._cacheTimestamp) < this._cacheTTL) {
            // Refresh only the timestamp
            return { ...this._cachedContext, timestamp: new Date().toISOString() };
        }

        const [networkInfo] = await Promise.all([
            this._getNetworkInfo()
        ]);

        const ctx = {
            timestamp: new Date().toISOString(),
            timezoneOffset: new Date().getTimezoneOffset(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,

            deviceFingerprint: deviceFingerprinting.getFingerprint(),

            connectionType: this._getConnectionType(),
            ip: networkInfo.ip,
            city: networkInfo.city,
            region: networkInfo.region,
            country: networkInfo.country,
            isp: networkInfo.isp,
            asn: networkInfo.asn,
        };

        this._cachedContext = ctx;
        this._cacheTimestamp = now;

        return ctx;
    }

    /**
     * Use a free, privacy-respecting IP geolocation API.
     * Falls back gracefully if blocked or offline.
     */
    async _getNetworkInfo() {
        const empty = { ip: null, city: null, region: null, country: null, isp: null, asn: null };

        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 4000);

            const res = await fetch('https://ipapi.co/json/', {
                signal: controller.signal,
                // Don't send cookies / auth headers to a third-party
                credentials: 'omit',
                mode: 'cors'
            });
            clearTimeout(timeout);

            if (!res.ok) return empty;

            const data = await res.json();
            return {
                ip: data.ip || null,
                city: data.city || null,
                region: data.region || null,
                country: data.country_name || null,
                isp: data.org || null,
                asn: data.asn || null
            };
        } catch {
            return empty;
        }
    }

    /**
     * Network connection type (wifi / cellular / ethernet / unknown).
     */
    _getConnectionType() {
        const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (!conn) return 'unknown';
        return conn.effectiveType || conn.type || 'unknown';
    }

}

const loginContextService = new LoginContextService();
export default loginContextService;
